/**
 * Button.
 *
 * Three variants and no more. Section 6.2 Principle 5 keeps the UI light, and a
 * screen with four competing button styles has stopped telling staff which action
 * is the important one.
 *
 *   primary     the one action this screen exists for
 *   secondary   everything else
 *   danger      destructive or irreversible (reject, cancel an assignment)
 */

import type { ButtonHTMLAttributes, ReactNode } from 'react';
import styles from './Button.module.css';

export type ButtonVariant = 'primary' | 'secondary' | 'danger';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  /** Shows a spinner and disables the button. Use for an in-flight request. */
  loading?: boolean;
  children: ReactNode;
}

export function Button({
  variant = 'secondary',
  loading = false,
  disabled,
  children,
  className,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      // Defaults to "button": an unlabelled <button> inside a <form> submits it,
      // which is a recurring source of accidental double submissions.
      type={type}
      className={[styles.button, styles[variant], className].filter(Boolean).join(' ')}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading && <span className={styles.spinner} aria-hidden="true" />}
      {children}
    </button>
  );
}
